<script lang="ts">
    import Vue from 'vue';

    export default Vue.extend({
        head: {
			title: { inner: 'Tennis Competition and League Management made easy', separator: '|', complement: 'Str8 Sets' },
            meta: [
				{ name: 'description', content: 'Do you spend far too much time organizing and running your tennis league? Goodbye paper and Excel score sheets and hello Str8 Sets! Set your rules, add players and publish. Participants can now add scores via the app and see the ladder live. Fill-ins are a breeze with a dedicated feature to handle emergency players.', id: 'desc'},
                { property: 'og:type', content: 'website' },
				{ property: 'og:url', content: 'https://www.str8sets.com/tennis_league_management' },
                { property: 'og:title', content: 'Tennis Competition and League Management made easy | Str8 Sets' },
                { property: 'og:description', content: 'Do you spend far too much time organizing and running your tennis league? Goodbye paper and Excel score sheets and hello Str8 Sets! Set your rules, add players and publish. Participants can now add scores via the app and see the ladder live. Fill-ins are a breeze with a dedicated feature to handle emergency players.' },
                { property: 'og:image', content: 'https://www.str8sets.com/img/meta_str8sets_comps_circle_matches_1200x628.jpg' },   
				{ property: 'og:image:alt', content: 'Str8 Sets Tennis Logo and App Screenshots' },            
                { name: 'twitter:site', content: 'str8sets' },
				{ name: 'twitter:creator', content: '@str8sets' },
				{ name: 'twitter:card', content: 'summary_large_image' },  
				{ name: 'twitter:url', content: 'https://www.str8sets.com/tennis_league_management' },
				{ name: 'twitter:title', content: 'Tennis Competition and League Management made easy | Str8 Sets' },
                { name: 'twitter:description', content: 'Do you spend far too much time organizing and running your tennis league? Goodbye paper and Excel score sheets and hello Str8 Sets! Set your rules, add players and publish. Participants can now add scores via the app and see the ladder live. Fill-ins are a breeze with a dedicated feature to handle emergency players.' },
				{ name: 'twitter:image', content: 'https://www.str8sets.com/img/meta_str8sets_comps_circle_matches_1200x628.jpg' },
				{ name: 'twitter:image:alt', content: 'Str8 Sets Tennis Logo and App Screenshots' }
            ],
            link: [
      			{ rel: 'canonical', href: 'https://www.str8sets.com/tennis_league_management', id: 'canonical' }
			]
        }
    })
</script>

<template>
    <div>
        <div class="parallax image-blue-courts-above height-350">
            <div class="container">
                <div class="feature-page-header">
                    <h1>Tennis League Management</h1>
                    <h2>Manage tennis competitions</h2>
                </div>
                <div class="row">
                    <router-link class="clear_button" to="/case-studies_competitions" role="button">VIEW COMPETITION CASE STUDIES</router-link>
                    <router-link class="clear_button" to="/faq#competition" role="button">FREQUENT QUESTIONS</router-link>
                </div>
            </div>
        </div>

        <section class="feature-list section">
            <div class="container">
                <div class="row">
                    <div class="col-md-12">
                        <div class="content mt-100">
                            <p><strong>We want competition organizers and players to focus on enjoying the competition and playing within it</strong>. 
                            We don't want them to worry about anything else. Gone are paper and Excel score sheets! With Str8 Sets, 
                            league competition management and participation is a breeze. Organizers setup the rules of the competition, 
                            add players, define teams and generate the draw via the website. Once published, the players participate 
                            and view the competition via the app. Scores are entered by the players as they complete their matches and 
                            the ladder updates live. Not to mention, our “Fill-in” feature allows team players to request an emergency 
                            player with a single click of a button! It's the kind of simplicity we strive for at Str8 Sets.</p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="spacer-large"></div>
            <div class="container">
                <div class="row">
                    <div class="col-md-12 heading">
                        <img class="icon" src="@/assets/images/comps.svg" alt="Green Competitions Icon" data-no-retina>
                        <h2>How it Works</h2>
                        <h4>The must have app for tennis competitions!</h4>
                    </div>                            
                </div>
                <div class="row">                               
                    <div class="col-md-8">
                        <div class="content mt-100">
                            <p>With this feature, competition organizers define the rules via the website. This includes playing dates, 
                            match formats, scoring rules and other information that govern the entire competition. Organizers also list the 
                            players and allocate them to their respective teams. Emergency players are also included. Based on this information, 
                            Str8 Sets will generate a draw for you automatically or let you do it manually. Once you're done, you can publish the 
                            competition, which results in notifying all your players they can view it via Str8 Sets on their mobile devices 
                            (iOS and Android).</p>
                            <p>During competition play, players can enter scores as they complete each match, view the “live” ladder and the competition 
                            rules via the app. Str8 Sets allows team and individual player ladders if relevant to your chosen competition format.</p>
                        </div>
                    </div>
                    <div class="col-md-4 text-center">
                        <img class="img_mobile_feature zoom-hover" :src="'/img/android_competitions_243x440.jpg'" alt="Str8 Sets App Screenshot (Android, Competitions)" v-rjs="2" />
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-12">
                        <div class="content mt-100">
                            <div class="text-heading">
                                <h2>Fill-in Requests</h2>
                            </div> 
                            <p>We know everyone is busy and life can throw surprises at us all the time. That's why we invented the Fill-in feature. 
                            Players belonging to a team on a Str8 Sets league competition who can't make it for a match, can request a fill-in for 
                            that day. With only a few clicks the request is sent to everyone not playing on that day, including emergency players. 
                            The request is received via the app and can be accepted on a first come first served basis.</p>
                            <p>This feature <strong>eliminates the need for messages, calls or emails to find a substitute</strong>. It's one of our 
                            most popular features for competitions</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <div class="parallax image-blue-courts-above height-150" ></div>

        <section class="feature-list section">
            <div class="container">
                <div class="row">
                    <div class="col-md-12">
                        <div class="content mt-100">
                            <div class="text-heading">
                                <h2>Communication</h2>
                            </div> 
                            <p>When you organize a Str8 Sets competition you manage all your needs in one place. This includes communications. 
                            From the app, you can send a message to the entire competition to inform them of any new events or cancellations. 
                            Messages are received in everyone's inbox via the app (push notifications). With this feature you don't need to 
                            maintain mailing lists, SMS groups or anything else!</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <div class="case-study-segue" id="studies">
            <div class="container">
                <div class="row">
                    <div class="col-md-12">
                        <h2>Do you want to see Str8 Sets in action?</h2>
                        <p>Let us show you.</p>
                        <p>Don't take our word for it, see for youself how clubs and competition organisers are using Str8 Sets to manage their leagues competitions.</p>
                        <div class="spacer"></div>
                        <router-link class="green_button" to="/case-studies_competitions" role="button">VIEW COMPETITION CASE STUDIES</router-link>
                    </div>
                </div>
            </div>
        </div>

        <section class="feature-list section">
            <div class="container">
                <div class="row text-center">
                    <img class="img_inrow" :src="'/img/android_competition_fillin_243x440.jpg'" alt="Str8 Sets App Screenshot (Android, Competition Fillin)" v-rjs="2" />
                    <img class="img_inrow" :src="'/img/android_competition_match_243x440.jpg'" alt="Str8 Sets App Screenshot (Android, Competition Match)" v-rjs="2" />
                    <img class="img_inrow" :src="'/img/android_competition_draw_243x440.jpg'" alt="Str8 Sets App Screenshot (Android, Competition Draw)" v-rjs="2" />
                </div> 
            </div>
        </section>

        <div class="parallax image-blue-courts-above height-150" ></div>


    </div>
</template>